import type { SearchPosting, Source } from "@/lib/api/catalogue";
import type { Application } from "@/lib/api/envoy";
import type { PostingFit } from "@/lib/api/resumeService";
import { excerpt, formatCount, formatRelative, postedAgo } from "@/lib/format";
import { whereLine } from "@/lib/openings";
import {
    applicationChip,
    dayOf,
    openingText,
    payLine,
    skipReasonWords,
    type StatusChip,
} from "@/lib/posting";
import { JOBS_PAGE } from "./view";

/**
 * The words after the count under the heading: "open postings match
 * “platform engineer”." The count itself is set in the figure's type.
 * Words searched from the resume's latest title read as any words do: a
 * suffix naming them wrapped the line a row deeper on a phone as that
 * search filled in, and moved the list under it.
 */
export function jobsLine(total: number | undefined, q: string): string {
    if (total === undefined) return q ? `Searching for “${q}”.` : "Reading the catalogue.";
    const noun = total === 1 ? "open posting" : "open postings";
    return q ? `${noun} ${total === 1 ? "matches" : "match"} “${q}”.` : `${noun} in the catalogue.`;
}

export interface JobCardView {
    postingId: string;
    title: string;
    company: string;
    /** The company's logo, for the card's corner. Null when the catalogue found none. */
    logoUrl: string | null;
    source: Source;
    /** The board's slug, which with the vendor names the company. */
    board: string;
    where: string;
    /** The pay it states, short for its chip: "$190k to $230k", "$50 to $60/hr". Null when none. */
    pay: string | null;
    chip: StatusChip;
    excerpt: string;
    posted: string | null;
    /** When the catalogue last changed the posting: its text, its close, or its listing again. */
    updated: string;
    /** The figure on the right: the fit, or when it went in, or why it was skipped. */
    aside: string | null;
    fit?: PostingFit | null;
}

/**
 * The words on the right of a card. Applied and skipped say what was
 * done; anything else the fit of the resume as it stands, when the
 * posting has been read and there is a resume to score.
 */
export function cardAside(
    application: Application | undefined,
    fit: PostingFit | null | undefined,
    timeZone?: string,
): string | null {
    if (application?.status === "APPLIED") {
        return `applied ${dayOf(application.applied_at ?? application.updated_at, timeZone)}`;
    }
    if (application?.status === "SKIPPED") {
        return `skipped: ${skipReasonWords(application.status_reason)}`;
    }
    if (!fit || fit.asked === 0) return null;
    return `${Math.min(fit.covered, fit.asked)} of ${fit.asked} keywords`;
}

export function jobCard(
    posting: SearchPosting,
    application: Application | undefined,
    fit: PostingFit | null | undefined,
    now: Date,
    timeZone?: string,
): JobCardView {
    return {
        postingId: posting.posting_id,
        title: posting.title,
        company: posting.company,
        logoUrl: posting.logo_url ?? null,
        source: posting.source,
        board: posting.board,
        where: whereLine(posting),
        pay: payLine(posting, true),
        chip: applicationChip(application, posting),
        excerpt: excerpt(openingText(posting.description), 150),
        posted: posting.posted_at ? postedAgo(posting.posted_at, now) : null,
        updated: formatRelative(posting.updated_at, now),
        aside: cardAside(application, fit, timeZone),
        fit: application?.status === "APPLIED" || application?.status === "SKIPPED" ? null : fit,
    };
}

/** "Showing 20 of 61", or "Showing 21 to 40 of 61" past the first page. */
export function showingLine(pageNumber: number, shown: number, total: number | undefined): string {
    const first = (pageNumber - 1) * JOBS_PAGE + 1;
    // Not past a total the page reaches: the last page stands in while the
    // next is read. A total under the page's start (counts that lag) is left
    // alone rather than turned into a range that runs backward.
    const end = first + shown - 1;
    const last = total !== undefined && total >= first ? Math.min(end, total) : end;
    // A later page left with none, such as an envoy page whose postings
    // the catalogue lost, says none rather than a range that runs backward.
    const range =
        pageNumber === 1 || shown === 0
            ? formatCount(shown)
            : `${formatCount(first)} to ${formatCount(last)}`;
    return total === undefined ? `Showing ${range}` : `Showing ${range} of ${formatCount(total)}`;
}
